import { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CONFIG } from '@hero/core';
import { usePageContext } from '@hero/web-context';
import {
  BillingPaywall,
  BillingWorkspaceLayout,
  CanceledBanner,
  CurrentPlanCard,
  EnterpriseUpgradeCard,
  PastDueBanner,
  RecentBillingsList,
} from '@hero/web-components';
import {
  route,
  useBillingOverviewManager,
  useBillingPayManager,
} from '@hero/web-hooks';

// NOTE(billing-addons): The `<AddonsList />` rendering has been
// intentionally removed for now — addons (WhatsApp integration, public
// API access, etc.) are still in the data model and the manager API,
// but we want to revisit the merchandising and pricing UX before
// surfacing them again on the overview. Re-import `AddonsList` from
// `@hero/web-components` and drop it back into the page when we pick
// the addons story back up.

/**
 * Landing screen for `/billing`.
 *
 * Three branches driven by `useBillingOverviewManager().viewMode`:
 *   - `paywall`  — past-due grace elapsed; only show the paywall.
 *   - `landing`  — no live subscription; redirect to `/billing/plan`
 *                  (the canonical plan picker) so the user gets the
 *                  full-width checkout instead of the narrower
 *                  `BillingWorkspaceLayout` card stack.
 *   - `overview` — live subscription; show the single-column rich card stack.
 *
 * The overview deliberately does NOT render `PaymentMethodCard` /
 * `TrialBanner` anymore: Moyasar handles payment per-billing via a
 * full-page redirect (no saved-card UI to manage), and the trial state
 * is folded into `<CurrentPlanCard />` itself so we don't show the
 * "Trial ends …" date twice on the same screen.
 *
 * Post-checkout success / failure banners used to live here, gated by
 * `?checkout=success|failed` query params. They've moved out into the
 * dedicated `BillingCheckoutReturnPage` so the user gets a real
 * confirmation screen (with a "Start working" CTA) instead of a
 * banner-on-overview.
 */
export default function BillingOverviewPage() {
  const { t } = useTranslation('m-billing');
  const { setPageName } = usePageContext();
  const manager = useBillingOverviewManager();
  const { payBilling } = useBillingPayManager();

  // TODO(billing-cancel-flow): Re-introduce a cancel manager here once
  // we wire up the cancel-at-period-end flow (proration, paid-period
  // grace, banner copy). For now `CurrentPlanCard` keeps the
  // `onCancel` prop on its API but doesn't render a button.
  // const cancelManager = useCancelSubscriptionManager({
  //   subscriptionId: manager.subscription?._id,
  //   onCanceled: () => manager.subscriptionQuery.refetch(),
  // });

  useEffect(() => {
    setPageName(t('page.title'));
  }, [setPageName, t]);

  const sub = manager.subscription;

  // An unsettled Billing (`pending` / `partially_paid` / `past_due`)
  // means there's a payment the user still needs to finish before we
  // promote anything else. Drives both the suppression of the
  // "Upgrade plan" CTA on `<CurrentPlanCard />` and the gating of the
  // enterprise upsell card below — we never want two purchase
  // prompts stacked on top of an open invoice.
  const hasPendingBilling = useMemo(
    () =>
      manager.recentBillings.some(
        b =>
          b.status === 'pending' ||
          b.status === 'partially_paid' ||
          b.status === 'past_due',
      ),
    [manager.recentBillings],
  );

  // Enterprise pricing isn't self-serve, so the next-tier prompt for
  // growth-plan orgs is a "Talk to sales" mailto rather than the
  // regular `/billing/plan` redirect. Keep this stable across
  // re-renders so the card doesn't get a fresh function identity
  // every paint.
  const handleContactSales = useMemo(
    () => () => {
      window.open(
        `mailto:${CONFIG.contact.salesEmail}?subject=${encodeURIComponent(
          'Hero — Enterprise upgrade inquiry',
        )}`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    [],
  );

  const showEnterpriseUpgrade =
    !!sub && sub.planCode === 'growth' && !hasPendingBilling;

  if (manager.viewMode === 'loading') {
    return (
      <BillingWorkspaceLayout>
        <BillingOverviewSkeleton />
      </BillingWorkspaceLayout>
    );
  }

  if (manager.viewMode === 'paywall') {
    return (
      <BillingWorkspaceLayout>
        <BillingPaywall
          onPayNow={manager.payNow}
          onResubscribe={manager.navigateToPlans}
        />
      </BillingWorkspaceLayout>
    );
  }

  if (manager.viewMode === 'landing') {
    // No live subscription → send the user to the dedicated plan
    // picker. `/billing/plan` mounts the same `<BillingCheckoutScreen />`
    // but full-width (no workspace cap), which is the right canvas for
    // a 3-column plan grid. Using `replace` so the back button skips
    // `/billing` and returns to wherever the user came from.
    return <Navigate to={route('billing_change_plan')} replace />;
  }

  return (
    <BillingWorkspaceLayout>
      <div className="flex flex-col gap-5 pb-12">
        {/* Actionable banners only — trial state is rendered inline on
            the plan card below. */}
        {sub?.status === 'past_due' && <PastDueBanner onPayNow={manager.payNow} />}
        {sub?.status === 'canceled' && (
          <CanceledBanner
            accessEndsAt={sub.currentPeriodEnd}
            onReactivate={manager.navigateToPlans}
          />
        )}

        <CurrentPlanCard
          subscription={sub}
          isLoading={manager.subscriptionQuery.isLoading}
          onSubscribe={manager.navigateToPlans}
          onChangePlan={manager.navigateToPlans}
          hasPendingBilling={hasPendingBilling}
          // Cancel CTA intentionally not wired — see comment above on
          // the cancelManager block.
        />

        {/*
          Growth-tier orgs see a dark "Upgrade to Enterprise" prompt
          instead of the standard upgrade button (which
          `<CurrentPlanCard />` suppresses for them). Hidden whenever
          a Billing is still unsettled — the user should finish the
          open payment before being asked to commit to another tier.
        */}
        {showEnterpriseUpgrade && (
          <EnterpriseUpgradeCard onContactSales={handleContactSales} />
        )}

        {/*
          TODO(billing-addons): Re-add `<AddonsList subscription={sub} />`
          here when we revisit the addons merchandising. The data and
          API still flow through `useBillingOverviewManager`, so this
          is purely a UI gating.
        */}

        <RecentBillingsList
          billings={manager.recentBillings}
          isLoading={manager.billingListQuery.isLoading}
          onSelect={b => manager.navigateToBilling(String(b._id))}
          onPay={b => payBilling(b, { openIn: 'tab' })}
          onViewAll={manager.navigateToHistory}
        />
      </div>
    </BillingWorkspaceLayout>
  );
}

/**
 * Skeleton placeholder shown while the subscription query is in flight.
 *
 * Visually matches the overview's card stack (header + plan card +
 * recent billings) so the layout doesn't jump when real data lands.
 */
function BillingOverviewSkeleton() {
  return (
    <div className="flex flex-col gap-5 pb-12" aria-busy="true" aria-live="polite">
      <SkeletonCard heightClassName="h-44" />
      <SkeletonCard heightClassName="h-56" />
    </div>
  );
}

function SkeletonCard({ heightClassName }: { heightClassName: string }) {
  return (
    <section
      className={`flex w-full animate-pulse flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm ${heightClassName}`}>
      <div className="flex items-center gap-3">
        <div className="size-10 shrink-0 rounded-xl bg-gray-100" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-4 w-32 rounded bg-gray-100" />
          <div className="h-3 w-20 rounded bg-gray-100" />
        </div>
        <div className="h-6 w-16 rounded-full bg-gray-100" />
      </div>
      <div className="grid grid-cols-1 gap-3 pt-1 sm:grid-cols-2">
        <div className="h-12 rounded-xl bg-gray-50" />
        <div className="h-12 rounded-xl bg-gray-50" />
      </div>
      <div className="h-10 w-32 rounded-md bg-gray-100" />
    </section>
  );
}
